import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Download, Package, AlertCircle, ChevronLeft, ChevronRight, ShoppingCart, ExternalLink } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { formatCurrency } from '../../utils/currency';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';
import UserLayout from './UserLayout';
import Card from '../shared/Card';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import StatusBadge from '../shared/StatusBadge';
import { Skeleton, SkeletonText } from '../shared/Skeleton';
import { QuantityInput, InCartNote } from '../shared/ProductParts';

const DEFAULT_PROMO_IMAGE = `${process.env.PUBLIC_URL}/mhs_promo.jpg`;

// Prices can arrive as numbers or numeric strings; anything else is "no price".
const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readCart = () => JSON.parse(localStorage.getItem('cart') || '[]');

const promoLabel = (promo, index) => promo.item_designation || promo.item_no || `Promo ${index + 1}`;

const PromoSkeleton = () => (
  <Card padding="none">
    <div className="px-6 py-5 border-b border-gray-200" role="status" aria-label="Loading promotion">
      <Skeleton className="h-6 w-72 max-w-full" />
      <Skeleton className="h-4 w-40 mt-2" />
      <span className="sr-only">Loading promotion…</span>
    </div>
    <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="order-2 lg:order-1 lg:col-span-5">
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
      <div className="order-1 lg:order-2 lg:col-span-7 space-y-6">
        <div>
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-9 w-40 mt-2" />
        </div>
        <SkeletonText lines={3} />
        <Skeleton className="h-11 w-56 max-w-full" />
      </div>
    </div>
  </Card>
);

const UserSlspPromo = () => {
  const history = useHistory();
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [assignedPromos, setAssignedPromos] = useState([]);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [inCartQty, setInCartQty] = useState(0);

  const promoData = assignedPromos[currentPromoIndex] || null;
  const itemNo = promoData?.item_no || null;

  // Each promo starts from a quantity of 1 and shows what's already in the cart.
  useEffect(() => {
    setQuantity(1);
    const line = itemNo ? readCart().find((item) => item.partNumber === itemNo) : null;
    setInCartQty(line ? line.quantity : 0);
  }, [itemNo]);

  useEffect(() => {
    fetchAssignedPromos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAssignedPromos = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('promo_assignments')
        .select(`
          promo_id,
          monthly_promos!fk_promo_assignments_promo_id (
            id,
            file_name,
            file_path,
            public_url,
            item_no,
            item_designation,
            description,
            special_net_price,
            slsp_net_price,
            promo_image_path,
            promo_image_url
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error && error.code !== 'PGRST116') throw error;

      if (data && data.length > 0) {
        // Filter to only show promos with actual product data (not PDF-only entries)
        const promos = data
          .map(item => item.monthly_promos)
          .filter(promo => promo && promo.item_no); // Only include promos with item_no

        if (promos.length > 0) {
          setAssignedPromos(promos);
          setCurrentPromoIndex(0);
        } else {
          // If no data promos, fetch the latest promo
          await fetchLatestPromo();
        }
      } else {
        // If no assigned promo, fetch the latest promo
        await fetchLatestPromo();
      }
    } catch (error) {
      console.error('Error fetching assigned promos:', error);
      setError('Failed to fetch assigned promos');
      // If error in fetching assigned promos, try to fetch the latest promo
      await fetchLatestPromo();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLatestPromo = async () => {
    try {
      const { data, error } = await supabase
        .from('monthly_promos')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      setAssignedPromos([data]);
      setCurrentPromoIndex(0);
    } catch (error) {
      console.error('Error fetching latest promo:', error);
      setError('Failed to fetch promo data');
    }
  };

  // Navigation handlers
  const goToPreviousPromo = () => {
    setCurrentPromoIndex((prev) =>
      prev === 0 ? assignedPromos.length - 1 : prev - 1
    );
  };

  const goToNextPromo = () => {
    setCurrentPromoIndex((prev) =>
      prev === assignedPromos.length - 1 ? 0 : prev + 1
    );
  };

  const handleDownloadPromo = async () => {
    try {
      if (!promoData || !promoData.file_path) throw new Error('No promo PDF available');

      setIsDownloading(true);

      const { data, error } = await supabase.storage
        .from('promos')
        .download(promoData.file_path);

      if (error) throw error;

      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = promoData.file_name;
      link.click();
      window.URL.revokeObjectURL(url);

      showNotification('Promo downloaded', 'success');
    } catch (error) {
      console.error('Error downloading promo:', error);
      showError(error, 'Failed to download promo PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  // The promo price is the customer's buy price for the month, so the cart line
  // carries exactly the price shown on this page.
  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!itemNo) return;
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }

    const cart = readCart();
    const existingIndex = cart.findIndex((item) => item.partNumber === itemNo);

    if (existingIndex >= 0) {
      cart[existingIndex].quantity += quantity;
    } else {
      cart.push({
        partNumber: itemNo,
        description: promoData.item_designation || promoData.description,
        quantity,
        price: slspPrice,
        isBOM: false,
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    notifyCartUpdated();
    setInCartQty(existingIndex >= 0 ? cart[existingIndex].quantity : quantity);
    showNotification(`${quantity} × ${itemNo} added to cart`, 'success');
  };

  const currentPeriod = new Date().toLocaleString('en-AU', { month: 'long', year: 'numeric' });

  const slspPrice = toNumber(promoData?.slsp_net_price);
  const specialPrice = toNumber(promoData?.special_net_price);
  // Only a genuine saving is shown; a promo priced at or above the special price isn't one.
  const savings = slspPrice !== null && specialPrice !== null && specialPrice > slspPrice
    ? {
        amount: specialPrice - slspPrice,
        percentage: Math.round(((specialPrice - slspPrice) / specialPrice) * 100),
      }
    : null;

  const hasMultiplePromos = assignedPromos.length > 1;

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SLSP Promo</h1>
            <nav className="text-sm text-gray-600 mt-1" aria-label="Breadcrumb">
              <button
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-gray-900 transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2" aria-hidden="true">{'>'}</span>
              <span aria-current="page">SLSP Promo</span>
            </nav>
            <p className="text-sm text-gray-600 mt-2">
              Exclusive monthly STAUFF pricing for MHS partners · {currentPeriod}
            </p>
          </div>

          {/* Promo switcher - only when there is more than one */}
          {hasMultiplePromos && !isLoading && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={goToPreviousPromo}
                ariaLabel="Previous promo"
                icon={<ChevronLeft className="w-5 h-5" aria-hidden="true" />}
              />
              <div className="flex-1 sm:flex-none min-w-0">
                <label htmlFor="promo-select" className="sr-only">Choose a promo</label>
                <select
                  id="promo-select"
                  value={currentPromoIndex}
                  onChange={(e) => setCurrentPromoIndex(parseInt(e.target.value, 10))}
                  className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                >
                  {assignedPromos.map((promo, index) => (
                    <option key={promo.id ?? index} value={index}>
                      {index + 1} of {assignedPromos.length}: {promoLabel(promo, index)}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                variant="secondary"
                onClick={goToNextPromo}
                ariaLabel="Next promo"
                icon={<ChevronRight className="w-5 h-5" aria-hidden="true" />}
              />
            </div>
          )}
        </div>

        {isLoading ? (
          <PromoSkeleton />
        ) : promoData ? (
          <Card padding="none">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{promoData.item_designation || promoData.item_no}</h2>
              <p className="text-sm text-gray-600 mt-1">
                Item number <span className="font-mono font-medium text-gray-900">{promoData.item_no}</span>
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Image sits below the price on mobile */}
              <div className="order-2 lg:order-1 lg:col-span-5">
                <img
                  src={promoData.promo_image_url || DEFAULT_PROMO_IMAGE}
                  alt={`Promotional artwork for ${promoLabel(promoData, currentPromoIndex)}`}
                  className="w-full h-auto rounded-lg border border-gray-200"
                  onError={(e) => {
                    // Fall back to the default artwork once; never loop if that fails too
                    e.target.onerror = null;
                    e.target.src = DEFAULT_PROMO_IMAGE;
                  }}
                />
              </div>

              <div className="order-1 lg:order-2 lg:col-span-7 space-y-6">
                {/* Price: the promo price leads, the price it replaces sits beside it */}
                <div>
                  <p className="text-sm font-medium text-gray-600">SLSP Net Price</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span className="text-3xl font-bold text-gray-900 tabular-nums">
                      {slspPrice !== null ? formatCurrency(slspPrice) : 'POA'}
                    </span>
                    {savings && (
                      <StatusBadge tone="success">
                        Save {formatCurrency(savings.amount)} ({savings.percentage}%)
                      </StatusBadge>
                    )}
                  </div>
                  {specialPrice !== null && (
                    <p className="text-sm text-gray-600 mt-1">
                      Special Net Price{' '}
                      <span className={`tabular-nums ${savings ? 'line-through' : ''}`}>{formatCurrency(specialPrice)}</span>
                    </p>
                  )}
                </div>

                {promoData.description && (
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-600">Description</h3>
                    <p className="text-gray-900 leading-relaxed mt-1">{promoData.description}</p>
                  </div>
                )}

                {/* Order at the promo price: the one primary action on the page */}
                <div className="pt-6 border-t border-gray-200 space-y-4">
                  <form onSubmit={handleAddToCart} className="flex flex-wrap items-end gap-3">
                    <QuantityInput id="promo-quantity" value={quantity} onChange={setQuantity} />
                    {orderingEnabled ? (
                      <Button
                        type="submit"
                        size="lg"
                        className="font-semibold"
                        icon={<ShoppingCart className="w-4 h-4" aria-hidden="true" />}
                      >
                        Add to Cart
                      </Button>
                    ) : (
                      <p className="text-sm font-medium text-gray-700 py-3">Online ordering is currently unavailable</p>
                    )}
                    {slspPrice !== null && quantity > 1 && (
                      <p className="text-sm text-gray-600 py-3 tabular-nums">
                        {quantity} × {formatCurrency(slspPrice)} ={' '}
                        <span className="font-semibold text-gray-900">{formatCurrency(slspPrice * quantity)}</span>
                      </p>
                    )}
                  </form>

                  <InCartNote quantity={inCartQty} onViewCart={() => history.push('/user/cart')} />

                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => history.push(`/user/product/${encodeURIComponent(itemNo)}`)}
                      icon={<ExternalLink className="w-4 h-4" aria-hidden="true" />}
                    >
                      View Part &amp; Stock
                    </Button>
                    {promoData.file_path && (
                      <Button
                        variant="secondary"
                        onClick={handleDownloadPromo}
                        loading={isDownloading}
                        icon={<Download className="w-4 h-4" aria-hidden="true" />}
                      >
                        {isDownloading ? 'Downloading…' : 'Download Branded Promo'}
                      </Button>
                    )}
                  </div>
                  {promoData.file_path && (
                    <p className="text-sm text-gray-600">
                      The branded promo is a PDF of this offer to share with your customers.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ) : error ? (
          <Card padding="none">
            <EmptyState
              icon={<AlertCircle className="h-12 w-12" />}
              title="The promotion couldn't be loaded"
              description="Something went wrong fetching this month's promo. Try again, or contact us if it keeps happening."
              action={<Button onClick={fetchAssignedPromos}>Try again</Button>}
            />
          </Card>
        ) : (
          <Card padding="none">
            <EmptyState
              icon={<Package className="h-12 w-12" />}
              title="No promotion available"
              description="Check back soon for this month's special offers."
            />
          </Card>
        )}

        {/* One disclaimer for the whole page */}
        <p className="text-xs text-gray-500">
          Promotional pricing is subject to availability, valid for the stated period only, and may change without notice.
          Contact our sales team for current pricing and availability.
        </p>
      </div>
    </UserLayout>
  );
};

export default UserSlspPromo;
